/**
 * Página de Criação de Equipamento - ConfereAI
 * Alinhada com a estrutura da tabela equipamentos do PostgreSQL
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import FormLayout from '../../../components/Common/FormLayout';

// Interface alinhada com a tabela equipamentos do PostgreSQL
interface EquipmentFormData {
  nome: string;
  tag: string;
  tipo: string;
  localizacao: string;
  status: 'ativo' | 'inativo' | 'manutencao';
  fabricante: string;
  numero_serie: string;
  capacidade: string;
}

const EquipmentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EquipmentFormData>({
    nome: '',
    tag: '',
    tipo: '',
    localizacao: '',
    status: 'ativo',
    fabricante: '',
    numero_serie: '',
    capacidade: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const tiposComuns = [
    'Extintor',
    'Mangueira',
    'Hidrante',
    'Alarme',
    'Detector de Fumaça',
    'Sinalização',
    'Iluminação de Emergência',
    'Kit Primeiros Socorros',
    'EPI',
    'Ferramenta',
    'Equipamento de Medição',
    'Outro'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name as keyof EquipmentFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.tag.trim()) {
      newErrors.tag = 'Tag é obrigatória';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Preparar dados para envio - remover campos vazios opcionais
      const dataToSend: any = {
        nome: formData.nome,
        tag: formData.tag,
        tipo: formData.tipo,
        status: formData.status,
        validade: null,
        descricao: null,
        responsavel: null
      };

      // Adicionar campos opcionais apenas se preenchidos
      if (formData.localizacao.trim()) {
        dataToSend.localizacao = formData.localizacao;
      }

      if (formData.fabricante.trim()) {
        dataToSend.fabricante = formData.fabricante;
      }

      if (formData.numero_serie.trim()) {
        dataToSend.numero_serie = formData.numero_serie;
      }

      if (formData.capacidade.trim()) {
        dataToSend.capacidade = formData.capacidade;
      }

      const response = await apiService.createEquipment(dataToSend) as ApiResponse<any>;

      if (response.success) {
        utils.showNotification('Equipamento criado com sucesso!', { type: 'success' });
        navigate('/equipamentos');
      } else {
        throw new Error(response.message || 'Erro ao criar equipamento');
      }
    } catch (error: any) {
      console.error('Erro ao criar equipamento:', error);
      utils.showNotification(error.message || 'Erro ao criar equipamento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/equipamentos');
  };

  return (
    <FormLayout
      title="Novo Equipamento"
      subtitle="Cadastrar novo equipamento no sistema"
      onSave={handleSubmit}
      onCancel={handleCancel}
      isLoading={isLoading}
      saveButtonText="Cadastrar Equipamento"
    >
      <div className="form-grid">
        {/* Nome */}
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="nome">Nome do Equipamento *</label>
          <input
            id="nome"
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleInputChange}
            className={`form-control ${errors.nome ? 'error' : ''}`}
            placeholder="Ex: Extintor CO2 - Recepção"
          />
          {errors.nome && <div className="error-message">{errors.nome}</div>}
        </div>

        {/* Tag */}
        <div className="form-group">
          <label htmlFor="tag">Tag/Código *</label>
          <input
            id="tag"
            type="text"
            name="tag"
            value={formData.tag}
            onChange={handleInputChange}
            className={`form-control ${errors.tag ? 'error' : ''}`}
            placeholder="Ex: EXT-001"
            maxLength={20}
          />
          {errors.tag && <div className="error-message">{errors.tag}</div>}
          <small style={{ display: 'block', color: '#7f8c8d', marginTop: '5px' }}>
            Código único de identificação (máx. 20 caracteres)
          </small>
        </div>

        {/* Tipo */}
        <div className="form-group">
          <label htmlFor="tipo">Tipo *</label>
          <input
            id="tipo"
            type="text"
            name="tipo"
            value={formData.tipo}
            onChange={handleInputChange}
            className={`form-control ${errors.tipo ? 'error' : ''}`}
            placeholder="Ex: Extintor"
            list="tipos-list"
            maxLength={50}
          />
          <datalist id="tipos-list">
            {tiposComuns.map(tipo => (
              <option key={tipo} value={tipo} />
            ))}
          </datalist>
          {errors.tipo && <div className="error-message">{errors.tipo}</div>}
        </div>

        {/* Localização */}
        <div className="form-group">
          <label htmlFor="localizacao">Localização</label>
          <input
            id="localizacao"
            type="text"
            name="localizacao"
            value={formData.localizacao}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Ex: Andar 2 - Sala 201"
            maxLength={100}
          />
        </div>

        {/* Status */}
        <div className="form-group">
          <label htmlFor="status">Status *</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="form-control"
          >
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
            <option value="manutencao">Manutenção</option>
          </select>
        </div>

        {/* Fabricante */}
        <div className="form-group">
          <label htmlFor="fabricante">Fabricante</label>
          <input
            id="fabricante"
            type="text"
            name="fabricante"
            value={formData.fabricante}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Fabricante do equipamento"
            maxLength={100}
          />
        </div>

        {/* Número de Série */}
        <div className="form-group">
          <label htmlFor="numero_serie">Número de Série</label>
          <input
            id="numero_serie"
            type="text"
            name="numero_serie"
            value={formData.numero_serie}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Número de série"
            maxLength={100}
          />
        </div>

        {/* Capacidade */}
        <div className="form-group">
          <label htmlFor="capacidade">Capacidade</label>
          <input
            id="capacidade"
            type="text"
            name="capacidade"
            value={formData.capacidade}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Ex: 10kg, 50L"
            maxLength={50}
          />
        </div>
      </div>
    </FormLayout>
  );
};

export default EquipmentCreate;
